// timeUtils.js
/**
 * Comprehensive time utility functions for handling UTC time across the application
 */

/**
 * Checks if a time string is in valid format (ISO or HH:MM:SS)
 * @param {string} timeString - Time string to validate
 * @returns {boolean} Whether the time string is valid
 */
export const isValidTimeFormat = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return false;

    // For ISO format
    if (timeString.includes('T')) {
        const date = new Date(timeString);
        return !isNaN(date.getTime());
    }

    // For HH:MM:SS format
    const regex = /^\d{2}:\d{2}:\d{2}$/;
    if (!regex.test(timeString)) return false;
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return false;
    if (hours < 0 || minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) return false;
    return true;
};

/**
 * Converts a time string to seconds
 * @param {string} timeString - Time string (ISO or HH:MM:SS)
 * @returns {number} Time in seconds
 */
export const timeToSeconds = (timeString) => {
    if (!isValidTimeFormat(timeString)) return 0;

    // Handle ISO string format
    if (timeString.includes('T')) {
        const timePart = timeString.split('T')[1].split('.')[0] || timeString.split('T')[1];
        const [hours, minutes, seconds] = timePart.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    }

    // Handle HH:MM:SS format
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Converts seconds to HH:MM:SS format
 * @param {number} totalSeconds - Time in seconds
 * @returns {string} Formatted time string
 */
export const secondsToTime = (totalSeconds) => {
    if (isNaN(totalSeconds)) return '00:00:00';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
};

/**
 * Ensures a number is padded with leading zeros
 * @param {number} num - Number to pad
 * @returns {string} Padded number string
 */
export const padZero = (num) => {
    return num.toString().padStart(2, '0');
};

/**
 * Adds two time strings (HH:MM:SS)
 * @param {string} time1 - First time string
 * @param {string} time2 - Second time string
 * @returns {string} Summed time string
 */
export const addTimeStrings = (time1, time2) => {
    try {
        if (!isValidTimeFormat(time1) || !isValidTimeFormat(time2)) return '00:00:00';
        const time1Seconds = timeToSeconds(time1);
        const time2Seconds = timeToSeconds(time2);
        return secondsToTime(time1Seconds + time2Seconds);
    } catch (error) {
        console.error('Error in addTimeStrings:', error);
        return '00:00:00';
    }
};

/**
 * Ensures a timestamp is a valid ISO string
 * @param {string} dateStr - Date string to validate and convert
 * @returns {string|null} Valid ISO string or null
 */
export const getISOString = (dateStr) => {
    try {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date.toISOString();
    } catch (error) {
        console.error('Error parsing date:', error);
        return null;
    }
};

/**
 * Gets current time as ISO string (UTC)
 * @returns {string} Current time as ISO string
 */
export const getCurrentISOTime = () => {
    return new Date().toISOString();
};

/**
 * Calculates time difference between a start time and now
 * @param {string} startTimeISO - Start time as ISO string
 * @returns {string} Time difference in HH:MM:SS format
 */
export const calculateTimeDifference = (startTimeISO) => {
    if (!startTimeISO) return '00:00:00';
    try {
        const start = new Date(startTimeISO);
        if (isNaN(start.getTime())) {
            console.error('Invalid date format:', startTimeISO);
            return '00:00:00';
        }

        const now = new Date();
        // Ensure positive difference
        const diff = Math.max(0, Math.floor((now - start) / 1000));
        return secondsToTime(diff);
    } catch (error) {
        console.error('Error calculating time difference:', error, startTimeISO);
        return '00:00:00';
    }
};

/**
 * Calculates duration between two ISO timestamps
 * @param {string} startDateTime - Start datetime as ISO string
 * @param {string} endDateTime - End datetime as ISO string
 * @returns {number} Duration in seconds
 */
export const calculateDurationInSeconds = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return 0;

    try {
        const start = new Date(startDateTime);
        const end = new Date(endDateTime);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            console.error('Invalid date format in calculateDurationInSeconds');
            return 0;
        }

        return Math.max(0, Math.floor((end - start) / 1000));
    } catch (error) {
        console.error('Error in calculateDurationInSeconds:', error);
        return 0;
    }
};

/**
 * Formats ISO datetime to display in local timezone
 * @param {string} isoString - ISO datetime string
 * @param {Object} options - Formatting options
 * @returns {string} Formatted local time
 */
export const formatISOToLocalTime = (timeString) => {
    try {
        if (!timeString) return '-';

        // Parse the datetime string
        const date = new Date(timeString);

        // Get user's browser locale
        const userLocale = navigator.language || 'en-US';

        // Format in user's local timezone using their locale
        return date.toLocaleTimeString(userLocale, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true // You might want to make this configurable too
        });
    } catch (error) {
        console.error('Error formatting time:', error);
        return '-';
    }
};

/**
 * Formats ISO datetime to display in local date
 * @param {string} isoString - ISO datetime string
 * @param {string} format - Date format ('short', 'medium', 'long')
 * @returns {string} Formatted local date
 */
export const formatISOToLocalDate = (isoString, format = 'short') => {
    if (!isoString) return '-';

    try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return '-';

        let options;
        switch (format) {
            case 'short':
                options = { day: '2-digit', month: '2-digit', year: 'numeric' };
                break;
            case 'medium':
                options = { day: '2-digit', month: 'short', year: 'numeric' };
                break;
            case 'long':
                options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
                break;
            default:
                options = { day: '2-digit', month: '2-digit', year: 'numeric' };
        }

        return date.toLocaleDateString(undefined, options);
    } catch (error) {
        console.error('Error formatting ISO to local date:', error);
        return '-';
    }
};

/**
 * Store timer data in localStorage with UTC consistency
 * @param {string} key - Storage key
 * @param {string} value - Value to store (usually ISO timestamp)
 * @param {string} employeeId - Employee ID
 */
export const storeTimerData = (key, value, employeeId) => {
    try {
        if (value === null || value === undefined) {
            localStorage.removeItem(`${key}_${employeeId}`);
        } else {
            localStorage.setItem(`${key}_${employeeId}`, value);
        }
    } catch (error) {
        console.error(`Error storing ${key}:`, error);
    }
};

/**
 * Retrieve timer data from localStorage
 * @param {string} key - Storage key
 * @param {string} employeeId - Employee ID
 * @param {string} defaultValue - Default value if not found
 * @returns {string} Stored value or default
 */
export const getTimerData = (key, employeeId, defaultValue = null) => {
    try {
        const value = localStorage.getItem(`${key}_${employeeId}`);
        return value !== null ? value : defaultValue;
    } catch (error) {
        console.error(`Error retrieving ${key}:`, error);
        return defaultValue;
    }
};

/**
 * Format MySQL datetime string to ISO string
 * @param {string} mysqlDatetime - MySQL datetime string (YYYY-MM-DD HH:MM:SS)
 * @returns {string} ISO datetime string
 */
export const mysqlDatetimeToISO = (mysqlDatetime) => {
    if (!mysqlDatetime) return null;
    try {
        // Replace space with T and add Z for UTC
        return mysqlDatetime.replace(' ', 'T') + 'Z';
    } catch (error) {
        console.error('Error converting MySQL datetime to ISO:', error);
        return null;
    }
};

const HAS_TZ_OFFSET_RX = /[+-]\d{2}:\d{2}$/;

const extractYmdFromWorkDate = (workDate) => {
    if (workDate === undefined || workDate === null || workDate === '') return null;
    if (workDate instanceof Date) {
        return isNaN(workDate.getTime()) ? null : workDate.toISOString().slice(0, 10);
    }
    const str = String(workDate).trim();
    const m = str.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

/**
 * Normalize DB/API timestamps to a UTC ISO string so the browser can format local time correctly.
 * DB stores UTC; values without an explicit zone must not be parsed as local wall time.
 *
 * @param {string|Date|number} value - time_start / time_stop from API
 * @param {string|Date} [workDate] - work_date when value is time-only (HH:MM:SS)
 * @returns {string|null}
 */
export const normalizeDbTimestampToUtcIso = (value, workDate) => {
    if (value === undefined || value === null || value === '') return null;
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value.toISOString();
    }
    const s0 = String(value).trim();
    if (!s0) return null;
    if (/z$/i.test(s0)) return s0;
    if (HAS_TZ_OFFSET_RX.test(s0)) {
        const d = new Date(s0);
        return isNaN(d.getTime()) ? null : d.toISOString();
    }
    if (s0.includes('T')) {
        return `${s0}Z`;
    }
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s0)) {
        return s0.replace(' ', 'T') + 'Z';
    }
    if (/^\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(s0)) {
        const ymd = extractYmdFromWorkDate(workDate);
        if (!ymd) return null;
        return `${ymd}T${s0}Z`;
    }
    try {
        const d = new Date(s0);
        if (isNaN(d.getTime())) return null;
        return d.toISOString();
    } catch {
        return null;
    }
};

/**
 * Updates a time percentage based on elapsed time and maximum allowed time
 * @param {string} currentTime - Current time in HH:MM:SS or ISO format
 * @param {number} maxTimeSeconds - Maximum time in seconds
 * @returns {number} Percentage (0-100)
 */
export const calculateTimePercentage = (currentTime, maxTimeSeconds) => {
    try {
        const timeSeconds = timeToSeconds(currentTime);
        return Math.min((timeSeconds / maxTimeSeconds) * 100, 100);
    } catch (error) {
        console.error('Error calculating time percentage:', error);
        return 0;
    }
};

/**
 * Formats a date for stat card footers (total work / overtime).
 * Treats YYYY-MM-DD as a calendar date in the local timezone (avoids UTC-off-by-one).
 * Also accepts ISO strings and Date objects.
 * @returns {string} DD-MM-YYYY or '-' if invalid
 */
export const formatStatCardFooterDate = (value) => {
    if (value == null || value === '') return '-';
    let y;
    let m;
    let d;
    if (typeof value === 'string') {
        const t = value.trim();
        const cal = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (cal) {
            y = parseInt(cal[1], 10);
            m = parseInt(cal[2], 10);
            d = parseInt(cal[3], 10);
        } else {
            const dt = new Date(t);
            if (Number.isNaN(dt.getTime())) return '-';
            y = dt.getFullYear();
            m = dt.getMonth() + 1;
            d = dt.getDate();
        }
    } else if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return '-';
        y = value.getFullYear();
        m = value.getMonth() + 1;
        d = value.getDate();
    } else {
        return '-';
    }
    return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
};